import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Badge, 
  Modal, 
  Form, 
  Alert,
  Row,
  Col,
  Container,
  Spinner
} from 'react-bootstrap';
import { 
  Plus, 
  FileText, 
  CheckCircle,
  Clock,
  RefreshCw,
  Eye
} from 'lucide-react';
import axiosInstance from 'helpers/api';
import dayjs from 'dayjs';
import './TransactionDashboard.css';

const TransactionDashboard = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [metrics, setMetrics] = useState({
    active: 0,
    pendingCompliance: 0,
    documentsThisWeek: 0,
    complianceScore: 0
  });

  // Fetch transactions on component mount
  useEffect(() => {
    fetchTransactions();
    fetchMetrics();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/transactions');
      setTransactions(response.data.transactions || []);
      showMessage('success', `Loaded ${response.data.count || 0} transactions`);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      showMessage('danger', 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const response = await axiosInstance.get('/transactions/metrics');
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };

  const showMessage = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const values = {
      type: formData.get('type'),
      client_name: formData.get('client_name'),
      lead_id: formData.get('lead_id'),
      property_id: formData.get('property_id'),
      notes: formData.get('notes')
    };

    try {
      const response = await axiosInstance.post('/transactions', values);
      if (response.data.success) {
        showMessage('success', `Transaction ${response.data.transaction.id} created successfully!`);
        setCreateModalVisible(false);
        e.target.reset();
        fetchTransactions();
        fetchMetrics();
        
        // Show transaction details
        setSelectedTransaction(response.data.transaction);
        setDetailsModalVisible(true);
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      showMessage('danger', 'Failed to create transaction');
    }
  };

  const viewTransactionDetails = async (transaction) => {
    try {
      const response = await axiosInstance.get(`/transactions/${transaction.id}`);
      setSelectedTransaction(response.data.transaction);
      setDetailsModalVisible(true);
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      showMessage('danger', 'Failed to load transaction details');
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      'pre_contract': 'primary',
      'showing': 'info',
      'under_contract': 'warning',
      'closing': 'secondary',
      'completed': 'success'
    };
    return colors[stage] || 'secondary';
  };

  const getComplianceColor = (status) => {
    const colors = {
      'pending': 'warning',
      'submitted': 'primary',
      'approved': 'success',
      'rejected': 'danger'
    };
    return colors[status] || 'secondary';
  };

  return (
    <Container fluid className="transaction-dashboard py-3">
      {showAlert && (
        <Alert variant={alertType} dismissible onClose={() => setShowAlert(false)}>
          {alertMessage}
        </Alert>
      )}

      {/* Metrics Row */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <CheckCircle className="text-success me-2" size={24} />
                <h3 className="mb-0 text-success">{metrics.active}</h3>
              </div>
              <Card.Text>Active Transactions</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <Clock className="text-warning me-2" size={24} />
                <h3 className="mb-0 text-warning">{metrics.pendingCompliance}</h3>
              </div>
              <Card.Text>Pending Compliance</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FileText className="text-primary me-2" size={24} />
                <h3 className="mb-0 text-primary">{metrics.documentsThisWeek}</h3>
              </div>
              <Card.Text>Documents This Week</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <h3 className={`mb-0 ${metrics.complianceScore >= 80 ? 'text-success' : 'text-warning'}`}>
                  {metrics.complianceScore}%
                </h3>
              </div>
              <Card.Text>Compliance Score</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <FileText className="me-2" />
              <h5 className="mb-0">Transaction Management</h5>
            </div>
            <div>
              <Button 
                variant="secondary" 
                size="sm" 
                className="me-2"
                onClick={fetchTransactions}
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <RefreshCw size={16} className="me-1" />
                    Refresh
                  </>
                )}
              </Button>
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => setCreateModalVisible(true)}
              >
                <Plus size={16} className="me-1" />
                Create Transaction
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {transactions.length === 0 && !loading ? (
            <Alert variant="info">
              <Alert.Heading>No Transactions Found</Alert.Heading>
              <p>Create your first transaction to get started</p>
              <hr />
              <Button 
                size="sm" 
                variant="primary"
                onClick={() => setCreateModalVisible(true)}
              >
                Create Now
              </Button>
            </Alert>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Type</th>
                  <th>Stage</th>
                  <th>Client</th>
                  <th>Compliance</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>
                      <Button 
                        variant="link" 
                        size="sm"
                        onClick={() => viewTransactionDetails(transaction)}
                      >
                        {transaction.id.substring(0, 8)}...
                      </Button>
                    </td>
                    <td>{transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}</td>
                    <td>
                      <Badge bg={getStageColor(transaction.stage)}>
                        {transaction.stage.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </td>
                    <td>{transaction.client_name || 'Unknown Client'}</td>
                    <td>
                      <Badge bg={getComplianceColor(transaction.compliance_status)}>
                        {transaction.compliance_status.toUpperCase()}
                      </Badge>
                    </td>
                    <td>{dayjs(transaction.created_at).format('MMM DD, YYYY')}</td>
                    <td>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-1"
                        onClick={() => viewTransactionDetails(transaction)}
                      >
                        <Eye size={16} className="me-1" />
                        View
                      </Button>
                      <Button 
                        variant="primary" 
                        size="sm"
                      >
                        <FileText size={16} className="me-1" />
                        Add Docs
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create Transaction Modal */}
      <Modal 
        show={createModalVisible} 
        onHide={() => setCreateModalVisible(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Transaction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateTransaction}>
            <Form.Group className="mb-3">
              <Form.Label>Transaction Type *</Form.Label>
              <Form.Select name="type" required>
                <option value="">Select type</option>
                <option value="purchase">Purchase</option>
                <option value="listing">Listing</option>
                <option value="rental">Rental</option>
                <option value="referral">Referral</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Client Name *</Form.Label>
              <Form.Control 
                type="text" 
                name="client_name"
                placeholder="Enter client name" 
                required 
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Lead ID (Optional)</Form.Label>
              <Form.Control 
                type="text" 
                name="lead_id"
                placeholder="Enter lead ID if available" 
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Property ID (Optional)</Form.Label>
              <Form.Control 
                type="text" 
                name="property_id"
                placeholder="Enter property ID if available" 
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                name="notes"
                placeholder="Any additional notes" 
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button 
                variant="secondary" 
                className="me-2"
                onClick={() => setCreateModalVisible(false)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Create Transaction
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Transaction Details Modal */}
      <Modal 
        show={detailsModalVisible} 
        onHide={() => setDetailsModalVisible(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Transaction Details - {selectedTransaction?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTransaction && (
            <div>
              <Alert variant="success">
                <Alert.Heading>Transaction Created Successfully!</Alert.Heading>
                <p>Transaction ID: {selectedTransaction.id}</p>
              </Alert>
              
              <Card className="mb-3">
                <Card.Header>Transaction Information</Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Type:</strong> {selectedTransaction.type}</p>
                      <p><strong>Stage:</strong> {' '}
                        <Badge bg={getStageColor(selectedTransaction.stage)}>
                          {selectedTransaction.stage}
                        </Badge>
                      </p>
                      <p><strong>Client ID:</strong> {selectedTransaction.client_id}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Compliance Status:</strong> {' '}
                        <Badge bg={getComplianceColor(selectedTransaction.compliance_status)}>
                          {selectedTransaction.compliance_status}
                        </Badge>
                      </p>
                      <p><strong>Created:</strong> {dayjs(selectedTransaction.created_at).format('MMMM DD, YYYY HH:mm')}</p>
                      <p><strong>Documents:</strong> {selectedTransaction.documents?.length || 0} attached</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>Next Steps</Card.Header>
                <Card.Body>
                  <div className="d-grid gap-2">
                    <Button variant="primary">
                      <FileText className="me-2" />
                      Add Documents to Transaction
                    </Button>
                    <Button variant="success">
                      <CheckCircle className="me-2" />
                      Submit for Compliance Review
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>
          <Button variant="primary">
            <FileText className="me-2" />
            Start Document Workflow
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TransactionDashboard;